import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { formatEUR, formatDate, formatMonth, parseAmount } from "../lib/format";
import { Warning, Clock, Envelope, CheckCircle, Calendar, X } from "@phosphor-icons/react";
import { toast } from "sonner";

function MarkPaidDialog({ item, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [paidDate, setPaidDate] = useState(today);
  const [actualAmount, setActualAmount] = useState(String(item?.amount ?? ""));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!paidDate) { toast.error("Zahlungsdatum fehlt"); return; }
    setSaving(true);
    try {
      const payload = {
        kind: item.kind,
        invoice_ref_id: item.invoice_ref_id,
        installment_index: item.installment_index ?? null,
        paid: true,
        paid_date: paidDate,
        actual_amount: actualAmount === "" ? null : parseAmount(actualAmount),
        notes: notes || "",
      };
      await api.post("/mahnwesen/mark-paid", payload);
      toast.success("Als bezahlt markiert");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte nicht als bezahlt markiert werden");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="mark-paid-dialog">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
          <div>
            <div className="label-eyebrow">Als bezahlt markieren</div>
            <div className="font-display font-bold text-lg mt-0.5">{item.customer_name}</div>
            <div className="text-xs text-[#5B6371]">
              {item.kind === "avgs" ? "AVGS-Rechnung" : `FBW-Rate #${item.installment_index}`}
              {" · offen "}{formatEUR(item.amount)}
            </div>
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#1F242E]" data-testid="mark-paid-close">
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5B6371]">Zahlungsdatum</label>
            <input type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)}
              className="field w-full mt-1" data-testid="mark-paid-date" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5B6371]">Tatsächlicher Betrag (€)</label>
            <input type="text" inputMode="decimal" value={actualAmount} onChange={(e) => setActualAmount(e.target.value)}
              className="field w-full mt-1" data-testid="mark-paid-amount" />
            <div className="text-[10px] text-[#94A3B8] mt-1">Weniger eintragen = Teilzahlung — der Rest bleibt offen (SOLL).</div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5B6371]">Notiz (optional)</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="field w-full mt-1" placeholder="z.B. Teilzahlung, Verrechnung …" data-testid="mark-paid-notes" />
          </div>
        </div>
        <div className="px-5 py-3 bg-[#F8FAFC] border-t border-[#E5E7EB] flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-md border border-[#E5E7EB] hover:bg-[#F1F5F9] text-[#5B6371] text-sm font-medium disabled:opacity-50"
            data-testid="mark-paid-cancel"
          >
            Abbrechen
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 rounded-md bg-[#0AA09E] hover:bg-[#088885] text-white text-sm font-medium disabled:opacity-50"
            data-testid="mark-paid-submit"
          >
            {saving ? "Speichere …" : "Als bezahlt markieren"}
          </button>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { key: "to_send", label: "Zu versenden", icon: Envelope, color: "#F59E0B" },
  { key: "waiting", label: "Wartend", icon: Clock, color: "#0AA09E" },
  { key: "overdue", label: "Überfällig", icon: Warning, color: "#FF6B5C" },
  { key: "paid_recent", label: "Bezahlt", icon: CheckCircle, color: "#0AA09E" },
];

export default function MahnwesenPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overdue");
  const [q, setQ] = useState("");
  const [payingItem, setPayingItem] = useState(null);   // {kind, invoice_ref_id, installment_index, amount, customer_name}
  const [busyId, setBusyId] = useState(null);           // Zeilen-ID während undo

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mahnwesen");
      setData(data);
    } catch (e) {
      toast.error("Konnte Rechnungsübersicht nicht laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const undoPaid = async (item) => {
    if (!confirm(item.kind === "avgs" ? "Alle bestätigten Zahlungen dieser AVGS-Rechnung zurücksetzen? Die Rechnung wird wieder komplett offen (SOLL)." : "Zahlung wirklich zurücksetzen? Rate wird wieder als offen markiert.")) return;
    setBusyId(item.id);
    try {
      await api.post("/mahnwesen/mark-paid", {
        kind: item.kind,
        invoice_ref_id: item.invoice_ref_id,
        installment_index: item.installment_index ?? null,
        paid: false,
      });
      toast.success("Zahlung zurückgesetzt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte nicht zurückgesetzt werden");
    } finally {
      setBusyId(null);
    }
  };

  const summary = data?.summary || {};
  const items = data?.[tab] || [];

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter((i) => (i.customer_name || "").toLowerCase().includes(s) || (i.measure_type || "").toLowerCase().includes(s));
  }, [items, q]);

  // Bezahlt: gruppiere nach Monat
  const paidGrouped = useMemo(() => {
    if (tab !== "paid_recent") return null;
    const map = {};
    for (const i of filtered) {
      const m = i.paid_month || "?";
      if (!map[m]) map[m] = { month: m, items: [], total: 0 };
      map[m].items.push(i);
      map[m].total += i.amount || 0;
    }
    return Object.values(map).sort((a, b) => b.month.localeCompare(a.month));
  }, [filtered, tab]);

  return (
    <section className="space-y-6" data-testid="mahnwesen-page">
      <div>
        <div className="label-eyebrow">Rechnungsverwaltung</div>
        <h1 className="font-display text-4xl font-bold mt-1">Mahnwesen & Rechnungs-Timeline</h1>
        <p className="text-sm text-[#5B6371] mt-2 max-w-3xl">
          Alle Rechnungen im Blick: was wann versandt werden muss, wann welches Geld fällig ist, was überfällig ist und wann welche Zahlung eingegangen ist.
        </p>
      </div>

      {/* KPI-Karten (klickbar zum Wechsel) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {TABS.map((t) => {
          const Icon = t.icon;
          const count = summary[`${t.key}_count`] || 0;
          const amount = summary[`${t.key}_amount`] || 0;
          const active = tab === t.key;
          return (
            <button
              type="button"
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`text-left rounded-lg p-4 border-2 transition-all ${active ? "bg-white shadow-sm" : "bg-white/60 hover:bg-white"}`}
              style={{ borderColor: active ? t.color : "#E5E7EB" }}
              data-testid={`mahnwesen-tab-${t.key}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} weight="duotone" style={{ color: t.color }} />
                <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">{t.label}</div>
              </div>
              <div className="font-display text-2xl font-bold" style={{ color: t.color }} data-testid={`mahnwesen-count-${t.key}`}>
                {count}
              </div>
              <div className="text-xs text-[#5B6371] mt-0.5">{formatEUR(amount)}</div>
            </button>
          );
        })}
      </div>

      {/* Suchfeld */}
      <div>
        <input
          type="text"
          placeholder="Nach Kundenname suchen..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="field w-full sm:max-w-md"
          data-testid="mahnwesen-search"
        />
      </div>

      {loading && <div className="text-sm text-[#5B6371]">Lade …</div>}

      {!loading && tab !== "paid_recent" && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="mahnwesen-table">
              <thead>
                <tr className="text-xs uppercase tracking-widest text-[#5B6371] border-b-2 border-[#E5E7EB] bg-[#F8FAFC]">
                  <th className="text-left px-4 py-3 font-medium">Kunde / Kurs</th>
                  <th className="text-left px-4 py-3 font-medium">Typ</th>
                  {tab === "to_send" && <th className="text-left px-4 py-3 font-medium">Kurs Ende</th>}
                  {tab === "to_send" && <th className="text-right px-4 py-3 font-medium">Tage seit Ende</th>}
                  {(tab === "waiting" || tab === "overdue") && <th className="text-left px-4 py-3 font-medium">Erwartetes Datum</th>}
                  {tab === "waiting" && <th className="text-right px-4 py-3 font-medium">Tage bis fällig</th>}
                  {tab === "overdue" && <th className="text-right px-4 py-3 font-medium">Tage überfällig</th>}
                  <th className="text-right px-4 py-3 font-medium">Betrag</th>
                  <th className="text-left px-4 py-3 font-medium">Kontakt</th>
                  <th className="text-right px-4 py-3 font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-[#94A3B8]">
                    ✓ Nichts zu tun in dieser Kategorie!
                  </td></tr>
                )}
                {filtered.map((it) => (
                  <tr key={it.id} className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]" data-testid={`mahnwesen-row-${it.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#1F242E]">{it.customer_name}</div>
                      {it.measure_type && <div className="text-[10px] text-[#94A3B8] mt-0.5">{it.measure_type}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        it.kind === "avgs" ? "bg-[#E6F7F7] text-[#0AA09E]" : "bg-[#FFF1EE] text-[#FF6B5C]"
                      }`}>
                        {it.kind === "avgs" ? "AVGS" : `FBW #${it.installment_index}`}
                      </span>
                    </td>
                    {tab === "to_send" && <td className="px-4 py-3 text-sm">{formatDate(it.mass_end_date)}</td>}
                    {tab === "to_send" && (
                      <td className="px-4 py-3 text-right">
                        <span className={`font-medium ${it.days_since_end > 14 ? "text-[#FF6B5C]" : "text-[#F59E0B]"}`}>
                          {it.days_since_end} Tage
                        </span>
                      </td>
                    )}
                    {(tab === "waiting" || tab === "overdue") && <td className="px-4 py-3 text-sm">{formatDate(it.expected_payment_date)}</td>}
                    {tab === "waiting" && (
                      <td className="px-4 py-3 text-right text-[#0AA09E] font-medium">
                        in {it.days_until_due} Tagen
                      </td>
                    )}
                    {tab === "overdue" && (
                      <td className="px-4 py-3 text-right">
                        <span className={`font-bold ${it.days_overdue > 30 ? "text-[#FF6B5C]" : "text-[#F59E0B]"}`}>
                          {it.days_overdue} Tage
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3 text-right" data-testid={`mahnwesen-amount-${it.id}`}>
                      <div className="font-display font-bold text-[#1F242E]">{formatEUR(it.amount)}</div>
                      {it.paid_so_far > 0 && <div className="text-[10px] text-[#0AA09E]">Teilzahlung erhalten: {formatEUR(it.paid_so_far)}</div>}
                      {it.invoice_part === "mid" && <div className="text-[10px] text-[#94A3B8]">Zwischenrechnung</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#5B6371]">
                      {it.jobcenter_name && <div>{it.jobcenter_name}</div>}
                      {it.customer_email && <a href={`mailto:${it.customer_email}`} className="text-[#0AA09E] hover:underline">{it.customer_email}</a>}
                    </td>
                    {(
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setPayingItem(it)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#0AA09E] hover:bg-[#088885] text-white text-xs font-medium transition-colors"
                          data-testid={`mahnwesen-mark-paid-${it.id}`}
                        >
                          <CheckCircle size={13} weight="bold" />
                          Bezahlt
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="bg-[#F8FAFC] border-t-2 border-[#E5E7EB] font-display font-bold text-sm">
                    <td className="px-4 py-3" colSpan={tab === "to_send" ? 4 : 3}>Summe ({filtered.length})</td>
                    <td className="px-4 py-3 text-right" data-testid="mahnwesen-total">
                      {formatEUR(filtered.reduce((s, i) => s + i.amount, 0))}
                    </td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Bezahlt-Ansicht: gruppiert nach Monat */}
      {!loading && tab === "paid_recent" && paidGrouped && (
        <div className="space-y-4">
          {paidGrouped.length === 0 && (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-10 text-center text-sm text-[#94A3B8]">
              Keine kürzlichen Zahlungen (letzte 120 Tage).
            </div>
          )}
          {paidGrouped.map((g) => (
            <div key={g.month} className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={`mahnwesen-paid-month-${g.month}`}>
              <div className="px-4 py-3 bg-[#E6F7F7] border-b border-[#0AA09E]/30 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar size={16} weight="duotone" className="text-[#0AA09E]" />
                  <span className="font-display font-bold text-[#0AA09E]">Geldeingang {formatMonth(g.month)}</span>
                </div>
                <div className="font-display text-lg font-bold text-[#0AA09E]">{formatEUR(g.total)}</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-widest text-[#5B6371] border-b border-[#E5E7EB] bg-[#F8FAFC]">
                      <th className="text-left px-4 py-2 font-medium">Kunde / Kurs</th>
                      <th className="text-left px-4 py-2 font-medium">Typ</th>
                      <th className="text-left px-4 py-2 font-medium">Zahlungsdatum</th>
                      <th className="text-right px-4 py-2 font-medium">Betrag</th>
                      <th className="text-right px-4 py-2 font-medium">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((it) => (
                      <tr key={it.id} className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]" data-testid={`mahnwesen-paid-row-${it.id}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-[#1F242E]">{it.customer_name}</div>
                          {it.measure_type && <div className="text-[10px] text-[#94A3B8]">{it.measure_type}</div>}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            it.kind === "avgs" ? "bg-[#E6F7F7] text-[#0AA09E]" : "bg-[#FFF1EE] text-[#FF6B5C]"
                          }`}>
                            {it.kind === "avgs" ? "AVGS" : `FBW #${it.installment_index}`}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-sm">{formatDate(it.paid_date)}</td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="font-display font-bold text-[#0AA09E]">{formatEUR(it.amount)}</div>
                          {it.is_partial && <div className="text-[10px] text-[#F59E0B]">Teilzahlung</div>}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => undoPaid(it)}
                            disabled={busyId === it.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#E5E7EB] hover:bg-[#F1F5F9] text-[#5B6371] text-xs transition-colors disabled:opacity-50"
                            data-testid={`mahnwesen-undo-${it.id}`}
                            title="Zahlung zurücksetzen"
                          >
                            Zurücksetzen
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {payingItem && (
        <MarkPaidDialog
          item={payingItem}
          onClose={() => setPayingItem(null)}
          onDone={() => { setPayingItem(null); load(); }}
        />
      )}
    </section>
  );
}
